import wavingHappily from 'assets/images/characters/CarolinaMariadeJesus/waving-happily.webp';
import wavingWithYourEyeClosed from 'assets/images/characters/CarolinaMariadeJesus/waving-with-your-eye-closed.webp';
import ElevatedButton from 'components/ElevatedButton';
import EmptyWarning from 'components/EmptyWarning';
import PageLoading from 'components/PageLoading';
import {UserComposition} from 'containers/EditorPage';
import useCompositionFeedback from 'features/useCompositionFeedback';
import useCompositionNavegation from 'features/useCompositionNavegation';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {
  fetchCompositionByIdRequest,
  getCompositionById,
  getCompositionFetchError,
  getIsLoadingCompositionById,
} from 'store/reducers/compositions';
import {
  createUserFeelingsRequest,
  fetchUserFeelingsRequest,
  getUserFeelingsData,
  UserFeelingsStateData,
} from 'store/reducers/userFeelings';
import {ApplicationState} from 'store/rootReducer';
import {DevolutiveStage} from 'store/services/userFeelings';
import useIndexedDB from 'utils/hooks/useIndexedDB';
import {CompositionDetailExtended} from 'utils/types/compositions';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {fetchWritePreparation} from 'APIs/letrusAPI/writePreparation';
import {updateHasSeenCompositionRequest} from 'APIs/letrusAPI/compositionReview';
import {isFocusModeFeedbackReleased} from 'contexts/FocusMode';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {useQueryParam} from 'utils/hooks';
import Chat from './Chat';
import ChatBubble from './ChatBubble';
import styles from './CompositionFeedback.module.scss';
import Dialog from './Dialog';
import ViewHeader from './ViewHeader';
import CompositionView from './views/CompositionView';
import FeedbackHomepage from './views/FeedbackHomepage';
import FeelingsView from './views/FeelingsView';
import GeneralReview from './views/GeneralReview';
import GradesView from './views/GradesView';
import WelcomeView from './views/WelcomeView';

export type Views =
  | 'apresentacao'
  | 'geral'
  | 'criterios'
  | 'escrita'
  | 'central'
  | 'sentimentos';

interface StateProps {
  composition: ImmutableMap<CompositionDetailExtended>;
  isLoadingComposition: boolean;
  compositionFetchError: boolean;
  userFeelings?: UserFeelingsStateData;
}

interface DispatchProps {
  fetchCompositionByIdRequest: typeof fetchCompositionByIdRequest;
  fetchUserFeelingsRequest: typeof fetchUserFeelingsRequest;
  createUserFeelingsRequest: typeof createUserFeelingsRequest;
}

type CompositionFeedbackProps = StateProps & DispatchProps;

function CompositionFeedback({
  composition,
  isLoadingComposition,
  compositionFetchError,
  fetchCompositionByIdRequest,
  fetchUserFeelingsRequest,
  userFeelings,
  createUserFeelingsRequest,
}: CompositionFeedbackProps): JSX.Element {
  const [showModal, setShowModal] = useState(true);
  const [showAfterFeelingsModal, setShowAfterFeelingsModal] = useState(false);
  const [localComposition, setLocalComposition] = useState<UserComposition>();
  const {composition_id, feedback_view} = useParams();
  const [userFeelingsStage, setUserFeelingsStage] = useState<DevolutiveStage>(
    'pre-composition review',
  );
  const compositionIDB = useIndexedDB<UserComposition>('LocalCompositions');
  const writePreparation = fetchWritePreparation(composition_id, {
    isPaused: () => !composition.get('uses_write_preparation'),
  });
  const {instructions, conversationList, generalScore, gradeList} =
    useCompositionFeedback({
      composition,
      writePreparation: writePreparation.data,
      userFeelings,
    });

  const navigate = useNavigate();
  const location = useLocation();
  const {trackEvent} = useTrackEvent();
  const updateHasSeenComposition = updateHasSeenCompositionRequest();
  const {toggleWriteToReWrite} = useCompositionNavegation({composition});

  const learningPathUserInstanceId = useQueryParam().get('trilha') ?? '';
  const {navigateToLearningPath, learningPath} = useLearningPathNavigation(
    learningPathUserInstanceId,
  );

  // Guard: when Focus Mode feedback is blocked, keep student in LP flow.
  useEffect(() => {
    const isBlockedFocusModeFeedback =
      learningPath?.uses_focus_mode &&
      !isFocusModeFeedbackReleased(learningPath);

    if (!isBlockedFocusModeFeedback) {
      return;
    }

    const shouldShowNewReviewed =
      learningPath?.status === 'finished' ||
      learningPath?.status === 'reviewed';

    if (shouldShowNewReviewed) {
      navigate(
        {
          pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/geral`,
          search: location.search,
        },
        {replace: true},
      );
      return;
    }

    navigateToLearningPath.currentStep();
  }, [learningPath, composition_id, location.search, navigate]);

  const BASE_PATH = `/atividades/escrita/${composition_id}/devolutiva`;

  const isLoading = isLoadingComposition || writePreparation.isLoading;
  const hasFetchError = compositionFetchError || writePreparation.error;
  const isReviewInProgress =
    !!composition.size && composition?.get('status') !== 'reviewed';
  const isTeacherReviewInProgress =
    composition?.get('status') === 'teacher revision pending';

  const isGradesViewAvailable = !!composition
    ?.get('reviews')
    ?.first()
    ?.get('review_grades')
    ?.first()
    ?.get('grade_item');

  const parentId = composition.get('parent_id');
  const childId = composition.get('child_id');

  const activeWriting = parentId ? 'activeRewriting' : 'activeWriting';

  const reviewCompositionId = composition?.get('reviews')?.first()?.get('id');

  const wasSeenByStudent = composition
    ?.get('reviews')
    ?.first()
    ?.get('seen_by_student');

  useEffect(() => {
    fetchCompositionByIdRequest(composition_id);
  }, []);

  useEffect(() => {
    if (feedback_view === 'sentimentos') {
      fetchUserFeelingsRequest(composition_id);
    }

    if (!feedback_view) {
      // if it has no view defined, loads FeedbackHomepage view
      navigate(
        {
          pathname: `${location.pathname}/central`,
          search: location.search,
        },
        {replace: true},
      );
    }
  }, [feedback_view]);

  useEffect(() => {
    if (
      feedback_view === 'sentimentos' &&
      userFeelings &&
      userFeelings?.composition
    ) {
      setUserFeelingsStage('during-composition review');
      setShowAfterFeelingsModal(true);
      return;
    }
  }, [feedback_view, userFeelings]);

  useEffect(() => {
    const isCompositionEmpty = !composition.get('composition_raw');

    if (isCompositionEmpty) {
      compositionIDB.getAll(composition_id).then((localCompositions) => {
        localCompositions?.forEach((composition) => {
          if (composition.finished) {
            setLocalComposition(composition);
          }
        });
      });
    }

    if (reviewCompositionId && !wasSeenByStudent) {
      updateHasSeenComposition.trigger({id: reviewCompositionId});

      trackEvent('test_review_not_seen_notification');
    }
  }, [composition]);

  function getWriteToReWrite() {
    if (childId || parentId) {
      toggleWriteToReWrite();
      return;
    }
  }

  return (
    <>
      <main className={styles.wrapper}>
        {isLoading && <PageLoading />}

        {composition.size && !isLoading && hasFetchError ? (
          <EmptyWarning subtitle="Esta correção não está disponível." />
        ) : (
          <>
            <ViewHeader
              title={composition?.getIn(['test', 'name'])}
              subtitle={composition?.getIn(['test', 'theme_name'])}
              path={
                feedback_view === 'central'
                  ? '/'
                  : `${BASE_PATH}/central${location.search}`
              }
              hasScrollShadow
              showTransitionButtons={
                feedback_view === 'central' &&
                composition.get('child_id_status') === 'reviewed'
              }
              activeWriting={activeWriting}
              toggleWriteToReWrite={getWriteToReWrite}
            />

            {feedback_view === 'central' && (
              <FeedbackHomepage
                composition={composition}
                viewList={[
                  {
                    path: `${BASE_PATH}/escrita`,
                    label: 'Meu texto',
                    icon: 'file-alt',
                    available: true,
                  },
                  {
                    path: `${BASE_PATH}/geral`,
                    label: 'Devolutiva geral',
                    icon: 'bullseye-arrow',
                    available:
                      !!conversationList.generalReview?.length &&
                      !isTeacherReviewInProgress,
                  },
                  {
                    path: `${BASE_PATH}/criterios`,
                    label: 'Devolutiva por critério',
                    icon: 'clipboard-list-check',
                    available:
                      isGradesViewAvailable && !isTeacherReviewInProgress,
                  },
                  {
                    path: `${BASE_PATH}/sentimentos`,
                    label: 'Minhas emoções',
                    icon: 'book-open',
                    available: true,
                  },
                ]}
              />
            )}

            {feedback_view === 'apresentacao' && (
              <WelcomeView
                compositionId={composition_id}
                studentFirstName={
                  composition?.getIn(['user', 'first_name']) ?? ''
                }
              />
            )}

            {feedback_view === 'sentimentos' && (
              <FeelingsView
                userFeelingsStage={userFeelingsStage}
                createUserFeelingsRequest={createUserFeelingsRequest}
              />
            )}

            {feedback_view === 'geral' && (
              <GeneralReview
                maxScore={generalScore.max}
                studentScore={generalScore.student}
                reviewMessageList={conversationList.generalReview}
              />
            )}

            {feedback_view === 'criterios' && (
              <GradesView
                gradeList={gradeList}
                currentMessageList={conversationList.currentGradeMessageList}
              />
            )}

            {feedback_view === 'escrita' && (
              <CompositionView
                compositionTitle={composition.get('title')}
                compositionRaw={
                  localComposition?.text ?? composition.get('composition_raw')
                }
                instructions={instructions}
                annotations={writePreparation.data?.annotations}
              />
            )}
          </>
        )}
      </main>

      <Dialog
        open={
          isReviewInProgress &&
          feedback_view !== 'apresentacao' &&
          feedback_view !== 'sentimentos' &&
          showModal &&
          !isLoadingComposition &&
          !childId
        }
      >
        <div className={styles.inProgressReviewModal}>
          <ChatBubble
            imageUrl={wavingWithYourEyeClosed}
            variant="character-bottom"
            message={conversationList.inProgressReviewMessage}
          />

          <span className={styles.buttonWrapper}>
            <ElevatedButton
              text="Certo!"
              variant="primary"
              onClick={() => setShowModal(false)}
            />
          </span>
        </div>
      </Dialog>

      <Dialog open={showAfterFeelingsModal}>
        <div className={styles.inProgressReviewModal}>
          <Chat
            imageUrl={wavingHappily}
            variant="character-bottom"
            messageList={conversationList.emotionMessage}
            stacked
            autoForward={{enabled: true}}
          />

          <span className={styles.buttonWrapper}>
            <ElevatedButton
              text="Responder"
              variant="primary"
              onClick={() => setShowAfterFeelingsModal(false)}
            />
          </span>
        </div>
      </Dialog>
    </>
  );
}

export default connect(
  (state: ApplicationState) => ({
    composition: getCompositionById(state),
    isLoadingComposition: getIsLoadingCompositionById(state),
    compositionFetchError: getCompositionFetchError(state),
    userFeelings: getUserFeelingsData(state),
  }),
  {
    fetchCompositionByIdRequest,
    fetchUserFeelingsRequest,
    createUserFeelingsRequest,
  },
)(CompositionFeedback);
